/**
 * StyledSelectDropdown Component
 *
 * A theme-aware select dropdown component with built-in field-level validation.
 * Uses Material-UI v7 Select component with dynamic theme integration.
 *
 * IMPORTANT:
 * - Uses ONLY theme tokens (NO hardcoded values)
 * - Handles validation at field level (NOT section level)
 * - Supports single and multiple selection
 * - Includes search functionality for large option sets
 * - Optimized with React.memo
 */

import React, { useState, useCallback, useMemo, useRef, useEffect, useId } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  Checkbox,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  useTheme,
  SxProps,
  Theme,
  SelectChangeEvent,
  OutlinedInput,
  FilledInput,
  Input,
  FormHelperText,
  InputAdornment
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import {
  validateFieldAsync,
  ValidationRule,
  ValidationError
} from '../../validation';

// ============================================================================
// COMPONENT INTERFACES
// ============================================================================

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  avatar?: React.ReactNode;
  description?: string;
}

export interface SelectOptionGroup {
  group: string;
  options: SelectOption[];
}

export interface StyledSelectDropdownProps {
  /**
   * Core Functionality
   */
  value: string | number | (string | number)[];
  onChange: (value: string | number | (string | number)[]) => void;
  options?: SelectOption[];
  groupedOptions?: SelectOptionGroup[];

  /**
   * Form Integration
   */
  name?: string;
  label?: string;
  placeholder?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;

  /**
   * Selection Configuration
   */
  multiple?: boolean;
  showSelectedCount?: boolean;
  maxSelections?: number;
  searchable?: boolean;
  clearable?: boolean;

  /**
   * Display Options
   */
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  searchPlaceholder?: string;
  emptyStateText?: string;

  /**
   * Validation (Field-Level Responsibility)
   */
  error?: boolean;
  errorMessage?: string;
  rules?: ValidationRule[];
  validateOnChange?: boolean;
  validateOnBlur?: boolean;
  onValidationChange?: (errors: ValidationError[]) => void;

  /**
   * Theme Integration (REQUIRED - no hardcoded styles)
   */
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  color?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Dropdown Configuration
   */
  maxHeight?: number;
  autoWidth?: boolean;
  fullWidth?: boolean;

  /**
   * Accessibility
   */
  'aria-label'?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;

  /**
   * Event Handlers
   */
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLDivElement>) => void;
  onOpen?: () => void;
  onClose?: () => void;
  onSearchChange?: (searchValue: string) => void;

  /**
   * Custom Styling (Theme-Based)
   */
  sx?: SxProps<Theme>;

  /**
   * Advanced Configuration
   */
  renderValue?: (selected: any) => React.ReactNode;
  renderOption?: (option: SelectOption, selected: boolean) => React.ReactNode;
  getOptionLabel?: (option: SelectOption) => string;
  getOptionValue?: (option: SelectOption) => string | number;
  isOptionEqualToValue?: (option: SelectOption, value: any) => boolean;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const StyledSelectDropdown = React.memo<StyledSelectDropdownProps>(({
  // Core props
  value,
  onChange,
  options = [],
  groupedOptions = [],
  name,
  label,
  placeholder,
  helperText,
  required = false,
  disabled = false,
  readOnly = false,

  // Selection configuration
  multiple = false,
  showSelectedCount = false,
  maxSelections,
  searchable = false,
  clearable = false,

  // Display options
  loading = false,
  loadingText = 'Loading...',
  noOptionsText = 'No options found',
  searchPlaceholder = 'Search options...',
  emptyStateText = 'No options available',

  // Validation
  error: externalError = false,
  errorMessage,
  rules = [],
  validateOnChange = false,
  validateOnBlur = true,
  onValidationChange,

  // Theme integration
  variant = 'outlined',
  size = 'medium',
  color = 'primary',

  // Dropdown configuration
  maxHeight = 300,
  autoWidth = false,
  fullWidth = true,

  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,

  // Event handlers
  onFocus,
  onBlur,
  onOpen,
  onClose,
  onSearchChange,

  // Custom styling
  sx,

  // Advanced configuration
  renderValue,
  renderOption,
  getOptionLabel = (option) => option.label,
  getOptionValue = (option) => option.value,
  isOptionEqualToValue = (option, val) => getOptionValue(option) === val,

  // Other props
  ...otherProps
}) => {
  const theme = useTheme();
  const reactId = useId();
  const fieldId = name || reactId;

  // ============================================================================
  // INTERNAL STATE
  // ============================================================================

  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [internalErrors, setInternalErrors] = useState<ValidationError[]>([]);
  const [maxSelectionsReached, setMaxSelectionsReached] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  // Combine external error with internal validation errors
  const hasError = externalError || internalErrors.length > 0;
  const displayErrorMessage = errorMessage || (internalErrors[0]?.message);

  // Generate dynamic helper text for max selections
  const dynamicHelperText = useMemo(() => {
    if (maxSelectionsReached && maxSelections && multiple) {
      return `Maximum ${maxSelections} selections allowed`;
    }
    return helperText;
  }, [maxSelectionsReached, maxSelections, multiple, helperText]);

  // Get all options (regular + grouped)
  const allOptions = useMemo(() => {
    const regularOptions = options || [];
    const groupedOpts = (groupedOptions || []).reduce((acc, group) => {
      return [...acc, ...group.options];
    }, [] as SelectOption[]);
    return [...regularOptions, ...groupedOpts];
  }, [options, groupedOptions]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return allOptions;

    return allOptions.filter(option =>
      getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [allOptions, searchValue, getOptionLabel]);

  // Filter grouped options based on search
  const filteredGroupedOptions = useMemo(() => {
    if (!searchValue.trim()) return groupedOptions;

    return groupedOptions.map(group => ({
      ...group,
      options: group.options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchValue.toLowerCase())
      )
    })).filter(group => group.options.length > 0);
  }, [groupedOptions, searchValue, getOptionLabel]);

  // Get selected options for display
  const selectedOptions = useMemo(() => {
    const values = Array.isArray(value) ? value : [value];
    return allOptions.filter(option =>
      values.some(val => isOptionEqualToValue(option, val))
    );
  }, [value, allOptions, isOptionEqualToValue]);

  // ============================================================================
  // VALIDATION LOGIC
  // ============================================================================

  const validateValue = useCallback(async (valueToValidate: any) => {
    if (rules.length === 0) return [];

    try {
      const errors = await validateFieldAsync(valueToValidate, rules, name || 'select');
      setInternalErrors(errors);
      onValidationChange?.(errors);
      return errors;
    } catch (error) {
      console.error('Validation error:', error);
      return [];
    }
  }, [rules, name, onValidationChange]);

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleChange = useCallback((event: SelectChangeEvent<string | number | (string | number)[]>) => {
    let newValue = event.target.value;

    // Handle multiple selection
    if (multiple) {
      if (typeof newValue === 'string') {
        newValue = newValue.split(',');
      }

      // Check max selections
      if (maxSelections && Array.isArray(newValue) && newValue.length > maxSelections) {
        // Set a flag to show helper text
        setMaxSelectionsReached(true);
        // Clear the flag after 3 seconds
        setTimeout(() => setMaxSelectionsReached(false), 3000);
        return; // Don't allow more than max selections
      }

      // Update max selections reached state based on current selection count
      if (maxSelections && Array.isArray(newValue)) {
        setMaxSelectionsReached(newValue.length >= maxSelections);
      }
    }

    onChange(newValue as any);

    // Validate on change if enabled
    if (validateOnChange) {
      validateValue(newValue);
    }
  }, [onChange, multiple, maxSelections, validateOnChange, validateValue]);

  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpen?.();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchValue(''); // Clear search when closing
    onClose?.();
  }, [onClose]);

  const handleBlur = useCallback((event: React.FocusEvent<HTMLDivElement>) => {
    onBlur?.(event);

    // Validate on blur if enabled
    if (validateOnBlur) {
      validateValue(value);
    }
  }, [onBlur, validateOnBlur, validateValue, value]);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearchValue(newSearchValue);
    onSearchChange?.(newSearchValue);
  }, [onSearchChange]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (open && searchable && searchInputRef.current) {
      // Delay focus slightly to ensure dropdown is rendered
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [open, searchable]);

  // ============================================================================
  // THEME-AWARE STYLING
  // ============================================================================

  const selectStyles = useMemo((): SxProps<Theme> => ({
    // Base styles using theme tokens
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: theme.palette.divider,
      },

      '&:hover fieldset': {
        borderColor: theme.palette.text.primary,
      },

      '&.Mui-focused fieldset': {
        borderColor: theme.palette[color].main,
      },

      '&.Mui-error fieldset': {
        borderColor: theme.palette.error.main,
      },

      '&.Mui-disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
        '& fieldset': {
          borderColor: theme.palette.action.disabled,
        },
      },
    },

    '& .MuiFilledInput-root': {
      backgroundColor: theme.palette.action.hover,

      '&:hover': {
        backgroundColor: theme.palette.action.focus,
      },

      '&.Mui-focused': {
        backgroundColor: theme.palette.action.hover,
      },

      '&.Mui-error': {
        backgroundColor: theme.palette.error.main,
        color: theme.palette.error.contrastText,
      },

      '& .MuiSelect-select': {
        color: theme.palette.text.primary,
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
    },

    '& .MuiInput-root': {
      '&:before': {
        borderBottomColor: theme.palette.divider,
      },

      '&:hover:not(.Mui-disabled):before': {
        borderBottomColor: theme.palette.text.primary,
      },

      '&.Mui-focused:after': {
        borderBottomColor: theme.palette[color].main,
      },

      '&.Mui-error:after': {
        borderBottomColor: theme.palette.error.main,
      },
    },

    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,

      '&.Mui-focused': {
        color: theme.palette[color].main,
      },

      '&.Mui-error': {
        color: theme.palette.error.main,
      },
    },

    '& .MuiSelect-icon': {
      color: theme.palette.text.secondary,
    },

    // Custom sx prop overrides
    ...sx,
  }), [theme, color, sx]);

  const menuStyles = useMemo((): SxProps<Theme> => ({
    '& .MuiPaper-root': {
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      maxHeight: maxHeight,
    },

    '& .MuiMenuItem-root': {
      color: theme.palette.text.primary,

      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },

      '&.Mui-selected': {
        backgroundColor: theme.palette.action.selected,

        '&:hover': {
          backgroundColor: theme.palette.action.selected,
        },
      },

      '&.Mui-disabled': {
        color: theme.palette.text.disabled,
      },
    },

    '& .MuiListSubheader-root': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightMedium,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  }), [theme, maxHeight]);

  // ============================================================================
  // RENDER VALUE FUNCTION
  // ============================================================================

  const defaultRenderValue = useCallback((selected: any) => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">{loadingText}</Typography>
        </Box>
      );
    }

    // Check if no value is selected (empty state)
    const isEmpty = !selected ||
      selected === '' ||
      (Array.isArray(selected) && selected.length === 0);

    if (isEmpty && placeholder) {
      // Filled variant needs special handling for placeholder visibility
      if (variant === 'filled') {
        return (
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              opacity: 0.7,
              fontStyle: 'italic',
            }}
          >
            {placeholder}
          </Typography>
        );
      }
      // Return plain string for other variants - let Material-UI handle styling
      return placeholder;
    }

    if (multiple && Array.isArray(selected)) {
      if (selected.length === 0) {
        return placeholder || '';
      }

      if (showSelectedCount) {
        return `${selected.length} selected`;
      }

      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedOptions.slice(0, 2).map((option) => (
            <Chip
              key={getOptionValue(option)}
              label={getOptionLabel(option)}
              size="small"
              sx={{
                backgroundColor: theme.palette.action.selected,
                color: theme.palette.text.primary,
              }}
            />
          ))}
          {selectedOptions.length > 2 && (
            <Chip
              label={`+${selectedOptions.length - 2} more`}
              size="small"
              sx={{
                backgroundColor: theme.palette.action.hover,
                color: theme.palette.text.secondary,
              }}
            />
          )}
        </Box>
      );
    }

    // Single selection
    const selectedOption = selectedOptions[0];
    return selectedOption ? getOptionLabel(selectedOption) : '';
  }, [
    loading, loadingText, placeholder, multiple, showSelectedCount,
    selectedOptions, getOptionLabel, getOptionValue, variant, theme
  ]);

  // ============================================================================
  // RENDER OPTIONS FUNCTION
  // ============================================================================

  const renderMenuItems = useCallback(() => {
    const items: React.ReactNode[] = [];

    // Add search field if searchable
    if (searchable) {
      items.push(
        <Box key="search" sx={{ p: 1, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <TextField
            ref={searchInputRef}
            value={searchValue}
            onChange={handleSearchChange}
            placeholder={searchPlaceholder}
            size="small"
            fullWidth
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.default,
              }
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
      );
    }

    // Handle loading state
    if (loading) {
      items.push(
        <MenuItem key="loading" disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2">{loadingText}</Typography>
          </Box>
        </MenuItem>
      );
      // Keep the current value(s) in range while options load: hidden items, so MUI does not warn about an
      // out-of-range value (the field itself still renders "Loading..." through renderValue).
      const current = (Array.isArray(value) ? value : [value]).filter((v) => v !== '' && v !== null && v !== undefined);
      current.forEach((v) => {
        items.push(<MenuItem key={`loading-current-${String(v)}`} value={v as string | number} sx={{ display: 'none' }} />);
      });
      return items;
    }

    // Handle empty state
    if (filteredOptions.length === 0 && filteredGroupedOptions.length === 0) {
      const emptyMessage = searchValue ? noOptionsText : emptyStateText;
      items.push(
        <MenuItem key="empty" disabled>
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}
          >
            {emptyMessage}
          </Typography>
        </MenuItem>
      );
      return items;
    }

    // Render regular options
    if (options.length > 0) {
      filteredOptions.forEach(option => {
        const optionValue = getOptionValue(option);
        const isSelected = multiple
          ? Array.isArray(value) && (value as (string | number)[]).includes(optionValue)
          : value === optionValue;

        // Disable unselected options when max selections reached
        const isDisabled = option.disabled ||
          (multiple && maxSelectionsReached && !isSelected);

        items.push(
          <MenuItem
            key={optionValue}
            value={optionValue}
            disabled={isDisabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              ...(isDisabled && multiple && maxSelectionsReached && !isSelected && {
                opacity: 0.5,
                cursor: 'not-allowed',
              }),
            }}
          >
            {multiple && (
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&.Mui-checked': {
                    color: theme.palette[color].main,
                  },
                }}
              />
            )}

            {option.icon && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {option.icon}
              </Box>
            )}

            {option.avatar && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {option.avatar}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1">
                {renderOption ? renderOption(option, isSelected) : getOptionLabel(option)}
              </Typography>
              {option.description && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block' }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>

            {!multiple && isSelected && (
              <CheckIcon
                sx={{
                  color: theme.palette[color].main,
                  ml: 'auto'
                }}
              />
            )}
          </MenuItem>
        );
      });
    }

    // Render grouped options
    filteredGroupedOptions.forEach(group => {
      items.push(
        <ListSubheader key={group.group}>
          {group.group}
        </ListSubheader>
      );

      group.options.forEach(option => {
        const optionValue = getOptionValue(option);
        const isSelected = multiple
          ? Array.isArray(value) && (value as (string | number)[]).includes(optionValue)
          : value === optionValue;

        // Disable unselected options when max selections reached
        const isDisabled = option.disabled ||
          (multiple && maxSelectionsReached && !isSelected);

        items.push(
          <MenuItem
            key={optionValue}
            value={optionValue}
            disabled={isDisabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              pl: 4, // Indent grouped options
              ...(isDisabled && multiple && maxSelectionsReached && !isSelected && {
                opacity: 0.5,
                cursor: 'not-allowed',
              }),
            }}
          >
            {multiple && (
              <Checkbox
                checked={isSelected}
                size="small"
                sx={{
                  color: theme.palette.text.secondary,
                  '&.Mui-checked': {
                    color: theme.palette[color].main,
                  },
                }}
              />
            )}

            {option.icon && (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {option.icon}
              </Box>
            )}

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="body1">
                {renderOption ? renderOption(option, isSelected) : getOptionLabel(option)}
              </Typography>
              {option.description && (
                <Typography
                  variant="caption"
                  sx={{ color: theme.palette.text.secondary, display: 'block' }}
                >
                  {option.description}
                </Typography>
              )}
            </Box>

            {!multiple && isSelected && (
              <CheckIcon
                sx={{
                  color: theme.palette[color].main,
                  ml: 'auto'
                }}
              />
            )}
          </MenuItem>
        );
      });
    });

    return items;
  }, [
    searchable, searchValue, searchPlaceholder, handleSearchChange, theme,
    loading, loadingText, filteredOptions, filteredGroupedOptions,
    noOptionsText, emptyStateText, options, multiple, value, color,
    getOptionValue, getOptionLabel, renderOption, maxSelectionsReached
  ]);

  // ============================================================================
  // INPUT COMPONENT SELECTION
  // ============================================================================

  const InputComponent = useMemo(() => {
    switch (variant) {
      case 'filled':
        return FilledInput;
      case 'standard':
        return Input;
      case 'outlined':
      default:
        return OutlinedInput;
    }
  }, [variant]);

  // ============================================================================
  // ACCESSIBILITY ATTRIBUTES
  // ============================================================================

  const accessibilityProps = useMemo(() => ({
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy || (displayErrorMessage ? `${fieldId}-error` : undefined),
    'aria-invalid': ariaInvalid || hasError,
    'aria-required': required,
  }), [ariaLabel, ariaDescribedBy, ariaInvalid, hasError, required, fieldId, displayErrorMessage]);

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      error={hasError}
      disabled={disabled || loading}
      required={required}
      sx={selectStyles}
      onFocus={onFocus}
      onBlur={handleBlur}
      {...otherProps}
    >
      {label && (
        <InputLabel
          id={`${fieldId}-label`}
          shrink={true}
          sx={{
            color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
          }}
        >
          {label}
        </InputLabel>
      )}

      <Select
        labelId={label ? `${fieldId}-label` : undefined}
        value={value}
        onChange={handleChange}
        onOpen={handleOpen}
        onClose={handleClose}
        multiple={multiple}
        input={<InputComponent label={label} />}
        renderValue={renderValue || defaultRenderValue}
        displayEmpty={true}
        IconComponent={ExpandMoreIcon}
        MenuProps={{
          PaperProps: {
            sx: menuStyles,
          },
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          autoFocus: false,
          disableScrollLock: true,
          disablePortal: false,
          keepMounted: false,
          marginThreshold: 8,
        }}
        {...accessibilityProps}
      >
        {renderMenuItems()}
      </Select>

      {(displayErrorMessage || dynamicHelperText) && (
        <FormHelperText
          id={displayErrorMessage ? `${fieldId}-error` : undefined}
          sx={{
            color: hasError
              ? theme.palette.error.main
              : maxSelectionsReached
                ? theme.palette.warning.main
                : theme.palette.text.secondary,
            marginLeft: theme.spacing(1.75),
            marginTop: theme.spacing(0.375),
          }}
        >
          {displayErrorMessage || dynamicHelperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function for React.memo optimization
  // Only re-render if relevant props change

  const relevantProps = [
    'value', 'error', 'errorMessage', 'disabled', 'loading', 'required',
    'label', 'placeholder', 'helperText', 'variant', 'size', 'color',
    'multiple', 'searchable'
  ] as const;

  for (const prop of relevantProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Props changed, re-render needed
    }
  }

  // Check options array (shallow comparison)
  if (prevProps.options !== nextProps.options) {
    if (!prevProps.options || !nextProps.options ||
        prevProps.options.length !== nextProps.options.length) {
      return false;
    }
    // Simple reference check for performance
    for (let i = 0; i < prevProps.options.length; i++) {
      if (prevProps.options[i] !== nextProps.options[i]) {
        return false;
      }
    }
  }

  // Check grouped options array (shallow comparison)
  if (prevProps.groupedOptions !== nextProps.groupedOptions) {
    if (!prevProps.groupedOptions || !nextProps.groupedOptions ||
        prevProps.groupedOptions.length !== nextProps.groupedOptions.length) {
      return false;
    }
  }

  // Check validation rules array (reference comparison only)
  if (prevProps.rules !== nextProps.rules) {
    return false;
  }

  // Check sx prop (reference comparison only for performance)
  if (prevProps.sx !== nextProps.sx) {
    return false;
  }

  return true; // Props haven't changed, skip re-render
});

StyledSelectDropdown.displayName = 'StyledSelectDropdown';

// ============================================================================
// EXPORTS
// ============================================================================

export { StyledSelectDropdown as default };